import { useEffect, useState } from 'react'

interface ServiceStatus {
  status: 'ok' | 'error'
  error?: string
}

interface HealthResponse {
  status: 'ok' | 'degraded'
  timestamp: string
  uptime: number
  services: {
    mongodb: ServiceStatus
    postgres: ServiceStatus
  }
}

type FetchState =
  | { stage: 'loading' }
  | { stage: 'error'; message: string }
  | { stage: 'done'; data: HealthResponse }

function Badge({ status }: { status: 'ok' | 'error' | 'loading' }) {
  const bg = status === 'ok' ? '#16a34a' : status === 'error' ? '#dc2626' : '#9ca3af'

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 9999,
        fontSize: 13,
        fontWeight: 600,
        background: bg,
        color: '#fff',
      }}
    >
      {status.toUpperCase()}
    </span>
  )
}

function Row({
  label,
  svc,
  borderBottom,
}: {
  label: string
  svc: ServiceStatus
  borderBottom?: boolean
}) {
  return (
    <tr style={borderBottom ? { borderBottom: '1px solid #f3f4f6' } : undefined}>
      <td style={{ padding: '10px 16px', fontWeight: 500 }}>{label}</td>
      <td style={{ padding: '10px 16px' }}>
        <Badge status={svc.status} />
      </td>
      <td style={{ padding: '10px 16px', color: '#6b7280', fontSize: 13 }}>{svc.error ?? '—'}</td>
    </tr>
  )
}

export function HealthPage() {
  const [state, setState] = useState<FetchState>({ stage: 'loading' })

  useEffect(() => {
    let cancelled = false

    fetch('/api/health')
      .then(async (res) => {
        const data: HealthResponse = await res.json()
        if (!cancelled) setState({ stage: 'done', data })
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setState({ stage: 'error', message: err instanceof Error ? err.message : String(err) })
      })

    return () => {
      cancelled = true
    }
  }, [])

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, sans-serif',
    background: '#f9fafb',
    padding: 24,
  }

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
    padding: '32px 40px',
    width: '100%',
    maxWidth: 560,
  }

  if (state.stage === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={{ marginTop: 0 }}>System Health</h1>
          <p style={{ color: '#6b7280' }}>Checking services…</p>
        </div>
      </div>
    )
  }

  if (state.stage === 'error') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={{ marginTop: 0 }}>System Health</h1>
          <p style={{ color: '#dc2626' }}>Could not reach API: {state.message}</p>
        </div>
      </div>
    )
  }

  const { data } = state

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>System Health</h1>
          <Badge status={data.status === 'ok' ? 'ok' : 'error'} />
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 16px',
                  color: '#6b7280',
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                Service
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 16px',
                  color: '#6b7280',
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                Status
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 16px',
                  color: '#6b7280',
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                Info
              </th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 16px', fontWeight: 500 }}>API</td>
              <td style={{ padding: '10px 16px' }}>
                <Badge status="ok" />
              </td>
              <td style={{ padding: '10px 16px', color: '#6b7280', fontSize: 13 }}>
                Uptime {Math.floor(data.uptime)}s
              </td>
            </tr>
            <Row label="MongoDB" svc={data.services.mongodb} borderBottom />
            <Row label="PostgreSQL" svc={data.services.postgres} />
          </tbody>
        </table>

        <p style={{ marginBottom: 0, marginTop: 20, fontSize: 12, color: '#9ca3af' }}>
          Last checked: {new Date(data.timestamp).toLocaleString()}
        </p>
      </div>
    </div>
  )
}
